import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AuthChoiceModal } from './AuthChoiceModal';
import { isGuestAllowed } from '../utils/guestMode';
import { clearAuthSession } from '../utils/authStorage';
import { hasMigrated, markMigrated, migrateGuestDataToCloud } from '../utils/cloudMigration';
import { probeCloudCapability } from '../utils/cloudCapability';
import { usePomodoro } from '../pomodoro/PomodoroContext';

const Header = styled.header<{ $isVisible: boolean }>`
  background-color: ${({ theme }) => theme.headerBg};
  box-shadow: ${({ theme }) => theme.shadow};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: transform 0.3s ease;
  transform: translateY(${props => props.$isVisible ? '0' : '-100%'});
  backdrop-filter: blur(10px);
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${({ theme }) => theme.text};
  text-decoration: none;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
`;

const NavLink = styled(Link)<{ $isActive?: boolean }>`
  color: ${({ theme, $isActive }) => ($isActive ? theme.primary : theme.textSecondary)};
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

const Main = styled.main`
  padding-top: 4rem;
  min-height: calc(100vh - 4rem);
`;

const Footer = styled.footer`
  background-color: ${({ theme }) => theme.footerBg};
  color: ${({ theme }) => theme.footerText};
  padding: 1rem;
  text-align: center;
  margin-top: 2rem;
`;

const SocialIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
  
  a {
    color: white;
    font-size: 1.5rem;
    transition: color 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      color: #ddd;
    }

    svg {
      width: 1.5rem;
      height: 1.5rem;
    }
  }
`;

const AvatarWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: auto;
`;

const AvatarBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.text};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  user-select: none;
`;

const Menu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 220px;
  margin-top: 10px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.hoverShadow};
  padding: 0.5rem;
  display: none;

  /* hover 桥：填补头像与菜单之间的间隙，避免鼠标移动时菜单瞬间消失 */
  &::before {
    content: "";
    position: absolute;
    top: -12px;
    left: 0;
    right: 0;
    height: 12px;
    background: transparent;
  }

  ${AvatarWrap}:hover & {
    display: block;
  }
`;

const MenuItem = styled.button`
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  background: transparent;
  color: ${({ theme }) => theme.text};
  border-radius: 10px;
  padding: 0.55rem 0.6rem;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
    border-color: ${({ theme }) => theme.border};
  }
`;

const MenuHint = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  padding: 0.45rem 0.6rem 0.25rem;
  font-size: 0.88rem;
`;

const NavAction = styled.button`
  color: ${({ theme }) => theme.textSecondary};
  background: transparent;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  font-weight: 500;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

const Toast = styled.div`
  position: fixed;
  top: 4.4rem;
  right: 1rem;
  z-index: 1200;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  border-radius: 12px;
  padding: 0.65rem 0.8rem;
  box-shadow: ${({ theme }) => theme.hoverShadow};
  max-width: min(520px, 92vw);
`;

const PomodoroMiniWrap = styled.button`
  position: fixed;
  top: 4.55rem; /* 导航栏下方（不要遮挡 Header） */
  right: 1.05rem;
  z-index: 990; /* 低于 Header(1000)，确保不遮挡导航栏 */
  width: 68px;
  height: 68px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.border};
  background: radial-gradient(120% 120% at 30% 25%, rgba(255, 255, 255, 0.22), rgba(255, 255, 255, 0) 60%),
    radial-gradient(110% 110% at 60% 70%, rgba(0, 0, 0, 0.18), rgba(0, 0, 0, 0) 62%),
    linear-gradient(145deg, #ff5a66, #d72d3a);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.06) inset,
    0 0 26px rgba(255, 78, 92, 0.55), /* 光晕 */
    0 10px 28px rgba(0, 0, 0, 0.24);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.08) inset,
      0 0 32px rgba(255, 78, 92, 0.7),
      0 12px 34px rgba(0, 0, 0, 0.26);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }
`;

const PomodoroMiniPanel = styled.div`
  position: fixed;
  top: 4.55rem;
  right: calc(1.05rem + 72px);
  z-index: 989;
  width: min(340px, 88vw);
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  border-radius: 14px;
  box-shadow: ${({ theme }) => theme.hoverShadow};
  padding: 0.7rem 0.75rem;
  display: none;

  /* hover 桥：填补面板与按钮之间的间隙 */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: -16px;
    width: 16px;
    height: 100%;
    background: transparent;
  }
`;

const PomodoroMiniGroup = styled.div`
  position: fixed;
  top: 4.55rem;
  right: 1.05rem;
  z-index: 990;

  &:hover ${PomodoroMiniPanel} {
    display: block;
  }
`;

const PomodoroMiniTitle = styled.div`
  font-weight: 900;
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
`;

const PomodoroMiniSub = styled.div`
  margin-top: 0.35rem;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.9rem;
  line-height: 1.4;
  word-break: break-word;
`;

const PomodoroMiniActions = styled.div`
  margin-top: 0.7rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const PomodoroMiniBtn = styled.button<{ $primary?: boolean }>`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme, $primary }) => ($primary ? theme.primary : theme.surfaceAlt)};
  color: ${({ theme, $primary }) => ($primary ? '#fff' : theme.text)};
  border-radius: 10px;
  padding: 0.45rem 0.6rem;
  cursor: pointer;
  font-weight: 700;

  &:hover {
    opacity: 0.95;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PomodoroMiniLeaf = styled.div`
  position: absolute;
  top: 10px;
  left: 50%;
  width: 22px;
  height: 14px;
  transform: translateX(-50%) rotate(-10deg);
  border-radius: 999px 999px 999px 0;
  background: linear-gradient(145deg, #4fe38e, #18b764);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.18);

  &::after {
    content: '';
    position: absolute;
    top: 5px;
    left: 7px;
    width: 9px;
    height: 2px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.55);
    transform: rotate(18deg);
  }
`;

const PomodoroMiniTime = styled.div`
  position: relative;
  color: rgba(255, 255, 255, 0.96);
  font-weight: 900;
  letter-spacing: 0.5px;
  font-size: 0.98rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.32);
  user-select: none;
  pointer-events: none;
`;

function formatMMSS(sec: number) {
  const s = Math.max(0, Math.floor(sec));
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

interface LayoutProps {
  children: React.ReactNode;
  themePreference?: 'system' | 'light' | 'dark';
  onToggleTheme?: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, themePreference = 'system', onToggleTheme }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isNavVisible, setIsNavVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const pomodoro = usePomodoro();

  const [authModal, setAuthModal] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [migrating, setMigrating] = useState(false);
  const [authTick, setAuthTick] = useState(0);

  const isLoggedIn = useMemo(() => !!localStorage.getItem('today_token'), [authTick]); // today_token = blog app token
  const guestOk = useMemo(() => isGuestAllowed(), [authTick]);
  const authedUser = useMemo(() => {
    try {
      const raw = localStorage.getItem('today_user');
      if (!raw) return null;
      const u = JSON.parse(raw) as any;
      return u?.id ? { id: Number(u.id), username: String(u.username ?? '') } : null;
    } catch {
      return null;
    }
  }, [authTick]);

  const brandTitle = useMemo(() => {
    const name = isLoggedIn ? (authedUser?.username?.trim() || 'User') : 'LBJ';
    return `${name}' Friday`;
  }, [isLoggedIn, authedUser?.username]);

  const refreshAuth = () => {
    setAuthTick((x) => x + 1);
    // 同 tab 内 localStorage 改变不会触发 storage 事件；用自定义事件通知 Today/Guides 等页面刷新登录态
    window.dispatchEvent(new Event('today-auth-changed'));
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > 300) {
        if (currentScrollY > lastScrollY) {
          setIsNavVisible(false);
        } else {
          setIsNavVisible(true);
        }
      } else {
        setIsNavVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  // 全站入口：优先弹出登录/注册/游客选择（直到选择游客或登录成功）
  useEffect(() => {
    if (isLoggedIn) return;
    if (guestOk) return;
    setAuthModal(true);
  }, [isLoggedIn, guestOk, location.pathname]);

  // 允许任意页面通过事件打开登录弹窗（例如账号页按钮）
  useEffect(() => {
    const handler = () => setAuthModal(true);
    window.addEventListener('open-auth-modal', handler as any);
    return () => window.removeEventListener('open-auth-modal', handler as any);
  }, []);

  // 首次登录：自动尝试把游客本地数据迁移到云端
  const tryAutoMigrate = async () => {
    if (!authedUser?.id) return;
    if (hasMigrated(authedUser.id)) return;
    setMigrating(true);
    try {
      const r = await migrateGuestDataToCloud(authedUser.id);
      markMigrated(authedUser.id);
      setToast(
        `已尝试迁移本地数据到云端：Today=${r.today}，原则新增${r.guidesCreated}条/更新${r.guidesUpdated}条` +
          (r.guidesFailed ? `（失败${r.guidesFailed}条）` : ''),
      );
      window.setTimeout(() => setToast(null), 4500);
    } finally {
      setMigrating(false);
    }
  };

  // 云端能力探测：如果后端未部署新接口（404），避免前端持续刷 404
  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    const run = async () => {
      const cap = await probeCloudCapability();
      if (cancelled) return;
      if (cap === 'unsupported') {
        // 不降级、不清 token：只提示服务器未升级（用户仍保持登录态）
        setToast('后端未部署云端接口（/api/today-data /api/user-guides）。请先更新服务器代码/容器后再使用云端同步与云端原则。');
        window.setTimeout(() => setToast(null), 6500);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const manualMigrate = async () => {
    if (!authedUser?.id) return;
    setMigrating(true);
    try {
      const r = await migrateGuestDataToCloud(authedUser.id);
      setToast(
        `导入完成：Today=${r.today}，原则新增${r.guidesCreated}条/更新${r.guidesUpdated}条` +
          (r.guidesFailed ? `（失败${r.guidesFailed}条）` : ''),
      );
      window.setTimeout(() => setToast(null), 4500);
    } finally {
      setMigrating(false);
    }
  };

  return (
    <>
      <AuthChoiceModal
        visible={authModal}
        onClose={() => {
          setAuthModal(false);
          refreshAuth();
        }}
        onAuthed={() => {
          refreshAuth();
          void tryAutoMigrate();
        }}
      />
      {toast ? <Toast>{toast}</Toast> : null}
      {pomodoro.boundTask && (pomodoro.running || pomodoro.sessionActive) && (
        <PomodoroMiniGroup>
          <PomodoroMiniPanel>
            <PomodoroMiniTitle>
              <span>番茄时钟</span>
              <span style={{ fontWeight: 700, opacity: 0.75, fontSize: '0.92rem' }}>
                {pomodoro.mode === 'focus' ? '专注' : '休息'} · {pomodoro.running ? '计时中' : '已暂停'}
              </span>
            </PomodoroMiniTitle>
            <PomodoroMiniSub>
              任务：<strong>{pomodoro.boundTask.title}</strong>
            </PomodoroMiniSub>
            <PomodoroMiniSub>剩余：{formatMMSS(pomodoro.remainingSec)}</PomodoroMiniSub>

            <PomodoroMiniActions>
              <PomodoroMiniBtn
                $primary
                onClick={() => {
                  if (!pomodoro.boundTask) return;
                  pomodoro.toggleRunning();
                }}
              >
                {pomodoro.running ? '暂停' : '继续'}
              </PomodoroMiniBtn>
              {pomodoro.mode === 'break' ? (
                <PomodoroMiniBtn onClick={() => pomodoro.completeOrSkipBreak()}>结束休息</PomodoroMiniBtn>
              ) : null}
              <PomodoroMiniBtn onClick={() => pomodoro.reset(pomodoro.mode)}>重置</PomodoroMiniBtn>
              <PomodoroMiniBtn
                onClick={async () => {
                  if (pomodoro.focusScreen) {
                    pomodoro.setFocusScreen(false);
                    try {
                      if (document.fullscreenElement) await document.exitFullscreen?.();
                    } catch {
                      // ignore
                    }
                    return;
                  }
                  try {
                    await document.documentElement.requestFullscreen?.();
                  } catch {
                    // ignore
                  }
                  pomodoro.setFocusScreen(true);
                  navigate('/today');
                }}
              >
                {pomodoro.focusScreen ? '退出专注屏' : '进入专注屏'}
              </PomodoroMiniBtn>
            </PomodoroMiniActions>
          </PomodoroMiniPanel>

          <PomodoroMiniWrap
            onClick={() => navigate('/today')}
            title={`番茄时钟（点击回到 Today）：${pomodoro.boundTask.title}`}
            aria-label="番茄时钟（点击回到 Today）"
          >
            <PomodoroMiniLeaf />
            <PomodoroMiniTime>{formatMMSS(pomodoro.remainingSec)}</PomodoroMiniTime>
          </PomodoroMiniWrap>
        </PomodoroMiniGroup>
      )}
      <Header $isVisible={isNavVisible}>
        <Nav>
          <Logo to="/">{brandTitle}</Logo>
          <NavLinks>
            <NavLink to="/" $isActive={location.pathname === '/'}>Home</NavLink>
            <NavLink to="/today" $isActive={location.pathname === '/today'}>Today</NavLink>
            {isLoggedIn ? <NavLink to="/assistant" $isActive={location.pathname === '/assistant'}>Assistant</NavLink> : null}
            <NavLink to="/chronicle" $isActive={location.pathname.startsWith('/chronicle')}>Chronicle</NavLink>
            <NavLink to="/blog" $isActive={location.pathname.startsWith('/blog')}>Blog</NavLink>
            <NavLink to="/guides" $isActive={location.pathname.startsWith('/guides')}>Guides</NavLink>
            <NavLink to="/gallery" $isActive={location.pathname === '/gallery'}>Gallery</NavLink>
            <NavLink to="/features" $isActive={location.pathname === '/features'}>Shop</NavLink>
            <NavLink to="/contact" $isActive={location.pathname === '/contact'}>Contact</NavLink>
            <AvatarWrap>
              <AvatarBtn
                onClick={(e) => {
                  e.preventDefault();
                  if (!isLoggedIn) {
                    setAuthModal(true);
                    return;
                  }
                  navigate('/account');
                }}
                title={isLoggedIn ? '进入账户' : '登录 / 注册'}
              >
                {isLoggedIn ? (authedUser?.username?.slice(0, 1)?.toUpperCase() || 'U') : '?'}
              </AvatarBtn>
              <Menu>
                {!isLoggedIn ? (
                  <>
                    <MenuHint>未登录</MenuHint>
                    <MenuItem onClick={() => setAuthModal(true)}>登录 / 注册</MenuItem>
                    <MenuItem
                      onClick={() => {
                        // 直接允许游客模式，避免被强制弹窗打断
                        localStorage.setItem('personal_blog.frontend.guest_ok.v1', '1');
                        refreshAuth();
                      }}
                    >
                      继续游客模式
                    </MenuItem>
                  </>
                ) : (
                  <>
                    <MenuHint>你好，{authedUser?.username || '用户'}</MenuHint>
                    <MenuItem onClick={() => navigate('/account')}>账户中心</MenuItem>
                    <MenuItem
                      onClick={() => {
                        clearAuthSession();
                        localStorage.setItem('personal_blog.frontend.guest_ok.v1', '1');
                        refreshAuth();
                      }}
                    >
                      退出登录
                    </MenuItem>
                  </>
                )}
              </Menu>
            </AvatarWrap>
            <NavLink
              to="#"
              onClick={(e) => {
                e.preventDefault();
                onToggleTheme?.();
              }}
              $isActive={false}
              title="切换主题：system → light → dark"
            >
              Theme: {themePreference}
            </NavLink>
            {isLoggedIn ? (
              <NavAction onClick={() => void manualMigrate()} title="把游客本地的 Today/原则 导入到云端" disabled={migrating}>
                {migrating ? '导入中…' : '导入到云端'}
              </NavAction>
            ) : null}
          </NavLinks>
        </Nav>
      </Header>
      {/* authTick 变化时强制 remount：切换用户/退出登录后尽量干净重置页面状态与缓存 */}
      <Main key={authTick}>{children}</Main>
      <Footer>
        <p>&copy; 2024 离了大谱. All rights reserved.</p>
        <SocialIcons>
          <a href="#" title="YouTube"><i className="fab fa-youtube"></i></a>
          <a href="#" title="Twitter"><i className="fab fa-twitter"></i></a>
          <a href="#" title="Facebook"><i className="fab fa-facebook"></i></a>
          <a href="#" title="Instagram"><i className="fab fa-instagram"></i></a>
          <a href="#" title="LinkedIn"><i className="fab fa-linkedin"></i></a>
          <a href="#" title="TikTok"><i className="fab fa-tiktok" style={{ fontSize: '1.2rem' }}></i></a>
          <a href="#" title="哔哩哔哩">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M18.223 3.086a1.25 1.25 0 0 1 0 1.768L17.08 5.996h1.17A3.75 3.75 0 0 1 22 9.746v7.5a3.75 3.75 0 0 1-3.75 3.75H5.75A3.75 3.75 0 0 1 2 17.246v-7.5a3.75 3.75 0 0 1 3.75-3.75h1.166L5.775 4.855a1.25 1.25 0 1 1 1.767-1.768l2.652 2.652c.079.079.145.165.198.257h3.213c.053-.092.12-.18.199-.258l2.651-2.652a1.25 1.25 0 0 1 1.768 0zm.027 5.42H5.75a1.25 1.25 0 0 0-1.25 1.25v7.5c0 .69.56 1.25 1.25 1.25h12.5c.69 0 1.25-.56 1.25-1.25v-7.5c0-.69-.56-1.25-1.25-1.25zm-10 2.5c.69 0 1.25.56 1.25 1.25v2.5a1.25 1.25 0 1 1-2.5 0v-2.5c0-.69.56-1.25 1.25-1.25zm7.5 0c.69 0 1.25.56 1.25 1.25v2.5a1.25 1.25 0 1 1-2.5 0v-2.5c0-.69.56-1.25 1.25-1.25z"/>
            </svg>
          </a>
          <a href="#" title="小红书">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none">
              <rect x="0" y="2" width="24" height="20" rx="3" fill="currentColor"/>
              <text x="12" y="17" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#333" fontFamily="Arial">Xiao</text>
            </svg>
          </a>
          <a href="#" title="Email"><i className="fas fa-envelope"></i></a>
        </SocialIcons>
      </Footer>
    </>
  );
};

export default Layout;
